import React, { useEffect } from 'react';
import { Asset } from 'contentful';
import { useSpring, animated, easings } from 'react-spring';

import { IFullBleedHeroFields } from '@/types/contentful';

import { getImageDataFromOptions } from '../../utils/contentful';
import { usePageDataContext } from '../../context/PageData';
import { TextWithOptions } from '../Text/TextWithOptions';

import CtaWidget from '../CtaWidget';

import PrivacyPolicyDisclaimer from 'src/components/PrivacyPolicyDisclaimer';
import GlImage from '../Image';
import AdditionalContent from '../AdditionalContent';
import { ModalRevealCTA } from '../ModalRevealCTA';
import { SocialShare } from '../LearningCenter/Components/ArticleHeader/SocialShare';

import cx from 'classnames';
import Image from 'next/image';
import { SmartText } from '../SmartText';
import { useVideo } from '~/hooks/useVideo';

export function FullBleedHero({
  headingText,
  headingTextOptions,
  desktopBackgroundImage,
  desktopBackgroundImageOptions,
  desktopForVideosDominantColor,
  mobileBackgroundImage,
  mobileBackgroundImageOptions,
  privacyPolicyDisclaimer,
  ctaWidget,
  additionalContent,
  returningUserHeadingText,
  shareIcons,
  animationType = 'opacity',
  animatedTextDecoration,
  badgeIcon,
  badgeText
}: IFullBleedHeroFields) {
  const { query: { gl_content, g_adgroupid, utm_source } = {}, isReturning } =
    usePageDataContext();

  // Check if assets are videos (based on content type)
  const isDesktopVideo =
    desktopBackgroundImage?.fields?.file?.contentType?.includes('mp4') ||
    desktopBackgroundImage?.fields?.file?.contentType?.includes('video');
  const isMobileVideo =
    mobileBackgroundImage?.fields?.file?.contentType?.includes('mp4') ||
    mobileBackgroundImage?.fields?.file?.contentType?.includes('video');

  const isDesktopImage =
    desktopBackgroundImage?.fields?.file?.contentType?.includes('image');
  const isMobileImage =
    mobileBackgroundImage?.fields?.file?.contentType?.includes('image');

  const desktopRawUrl = isDesktopVideo
    ? desktopBackgroundImage?.fields?.file?.url || ''
    : '';
  const mobileRawUrl = isMobileVideo
    ? mobileBackgroundImage?.fields?.file?.url || ''
    : '';

  // Infer sensible posters: use the non-video counterpart if available
  const desktopPosterUrl =
    isDesktopVideo && isMobileImage
      ? mobileBackgroundImage?.fields?.file?.url
      : undefined;
  const mobilePosterUrl =
    isMobileVideo && isDesktopImage
      ? desktopBackgroundImage?.fields?.file?.url
      : undefined;

  const desktopUrl = desktopRawUrl;
  const mobileUrl = mobileRawUrl;

  const { videoRef: desktopVideoRef, sourceUrl: desktopSourceUrl } = useVideo(
    desktopUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );
  const { videoRef: mobileVideoRef, sourceUrl: mobileSourceUrl } = useVideo(
    mobileUrl,
    { manageVisibility: false, deferUntilVisible: true }
  );

  useEffect(() => {
    const videos: HTMLVideoElement[] = [
      desktopVideoRef.current,
      mobileVideoRef.current
    ].filter(Boolean) as HTMLVideoElement[];
    if (!videos.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) {
      videos.forEach((v) => v.pause());
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) {
            if (!v.ended) {
              v.play().catch(() => undefined);
            }
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, [desktopUrl, mobileUrl]);

  // Derive poster from the first frame if no poster is provided
  useEffect(() => {
    const listeners: Array<{ el: HTMLVideoElement; fn: () => void }> = [];

    const makePoster = (video: HTMLVideoElement | null) => {
      if (!video || video.poster) return;

      const drawFirstFrame = () => {
        if (video.poster) return;
        const width = video.videoWidth || 1;
        const height = video.videoHeight || 1;
        if (!width || !height) return;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        try {
          ctx.drawImage(video, 0, 0, width, height);
          const url = canvas.toDataURL('image/jpeg', 0.7);
          if (url) {
            video.poster = url;
          }
        } catch (_e) {
          // Ignore cross-origin canvas errors
        }
      };

      if (video.readyState >= 2) {
        drawFirstFrame();
      } else {
        const onLoaded = () => {
          drawFirstFrame();
          video.removeEventListener('loadeddata', onLoaded);
        };
        video.addEventListener('loadeddata', onLoaded);
        listeners.push({ el: video, fn: onLoaded });
      }
    };

    makePoster(desktopVideoRef.current);
    makePoster(mobileVideoRef.current);

    return () => {
      listeners.forEach(({ el, fn }) =>
        el.removeEventListener('loadeddata', fn)
      );
    };
  }, [desktopVideoRef, mobileVideoRef, desktopPosterUrl, mobilePosterUrl]);

  // Animation for headingText (only if animationType is 'opacity')
  const headingProps = useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(20px)' },
    delay: animationType !== 'opacity' ? 0 : 400,
    config: {
      duration: 3000,
      easing: easings.easeOutExpo
    },
    immediate: animationType !== 'opacity' // Disable animation if typewriter is selected
  });

  // Animation for CTA and Privacy Policy (this will always use useSpring)
  const ctaProps = useSpring({
    to: { opacity: 1, transform: 'translateY(0)' },
    from: { opacity: 0, transform: 'translateY(20px)' },
    delay: animationType !== 'opacity' ? 0 : 700,
    config: {
      duration: 3000,
      easing: easings.easeOutExpo
    },
    immediate: animationType !== 'opacity'
  });

  // Determine the heading content
  const headingContent = (
    <animated.div style={headingProps}>
      <TextWithOptions
        as="h1"
        v2
        className={cx(
          animatedTextDecoration
            ? 'mb-4 text-left text-heading2 font-normal leading-[46px] text-white tablet:!text-[40px] tablet:!leading-[48px] desktop:!text-[4rem] desktop:!leading-[74px] [@media(width>=375px)]:text-[32px] [@media(width>=375px)]:leading-[38px]'
            : 'mb-8 max-w-[350px] text-left text-5xl !leading-[115%] text-white tablet:mb-4 tablet:max-w-[644px] tablet:text-6xl'
        )}
        data-testid="fullbleed-hero-headline"
        keyword={gl_content}
        channel={utm_source}
        adGroupId={g_adgroupid}
        fallback={
          (isReturning
            ? returningUserHeadingText ?? headingText
            : headingText) || ''
        }
        options={headingTextOptions}
        module="FullBleed Hero heading"
      />
    </animated.div>
  );

  return (
    <div
      data-blockid="full-bleed-hero"
      data-testid="full-bleed-hero"
      data-e2e="hero"
      className=""
    >
      <div className="relative grid w-full desktop:max-h-[85vh]">
        {/* Desktop */}
        <div
          className={
            'hidden w-full max-w-[100vw] desktop:flex desktop:max-h-[85vh] desktop:min-h-[650px]'
          }
          style={{ gridArea: '1/1', background: desktopForVideosDominantColor }}
        >
          {isDesktopVideo ? (
            <video
              ref={desktopVideoRef}
              autoPlay
              muted
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              poster={desktopPosterUrl}
              id="desktop-video-background"
              className="h-full w-full object-cover object-center [@media(width>=1751px)]:object-contain"
            >
              <source src={desktopSourceUrl} type="video/mp4" />
            </video>
          ) : (
            <GlImage
              {...getImageDataFromOptions(
                gl_content,
                utm_source,
                g_adgroupid,
                desktopBackgroundImage as Asset,
                desktopBackgroundImageOptions
              )}
              className="aspect-[1440/700] h-full w-full object-top"
              loading="eager"
              heroImage={true}
              layout="fill"
              objectFit="cover"
              width={undefined}
              height={undefined}
              alt="desktop-background-image"
            />
          )}
        </div>
        {/* Mobile */}
        <div
          className="block max-h-[740px] tablet:max-h-[876px] desktop:hidden"
          style={{ gridArea: '1/1', background: desktopForVideosDominantColor }}
        >
          {isMobileVideo ? (
            <video
              ref={mobileVideoRef}
              autoPlay
              muted
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
              poster={mobilePosterUrl}
              className="aspect-[828/1200] h-full w-full object-cover object-top [@media(width>=635px)]:mx-auto [@media(width>=635px)]:w-[635px] [@media(width>=635px)]:max-w-[635px]"
            >
              <source src={mobileSourceUrl} type="video/mp4" />
            </video>
          ) : (
            <div className="aspect-[1440/2096] h-full w-full object-fill object-center">
              <GlImage
                {...getImageDataFromOptions(
                  gl_content,
                  utm_source,
                  g_adgroupid,
                  mobileBackgroundImage as Asset,
                  mobileBackgroundImageOptions
                )}
                loading="eager"
                heroImage={true}
                layout="fill"
                objectFit="cover"
                objectPosition={'top'}
                alt="mobile-background-image"
              />
            </div>
          )}
        </div>
        <div
          style={{
            gridArea: '1/1',
            position: 'relative',
            placeItems: 'center',
            display: 'grid'
          }}
        >
          <div className="block-container block h-full w-full !justify-normal desktop:flex desktop:flex-col">
            <div className="relative mt-auto flex h-full w-full max-w-[100vw] px-5 pb-5 align-bottom tablet:px-10 tablet:pb-10 desktop:px-15 desktop:pb-15">
              {badgeIcon && (
                <div
                  className="
                  absolute top-14 hidden size-[120px] items-center justify-center
                  desktop:block
                  "
                >
                  <Image
                    src={badgeIcon?.fields?.file?.url}
                    width={badgeIcon?.fields?.file?.details?.image?.width}
                    height={badgeIcon?.fields?.file?.details?.image?.height}
                    alt="Full Bleed Badge"
                    className="absolute left-0 top-0 z-[1] h-full w-full animate-spin object-contain [animation-duration:20000ms]"
                    loading="eager"
                    priority
                  />
                  {badgeText && (
                    <SmartText>
                      <div className="typography-title-3 relative z-[2] mx-auto flex h-full w-[100px] items-center justify-center text-center font-medium leading-[115%]">
                        {badgeText}
                      </div>
                    </SmartText>
                  )}
                </div>
              )}
              <div className="w-full self-end desktop:max-w-[655px]">
                {headingContent}
                <animated.div
                  style={animationType === 'opacity' ? ctaProps : {}}
                >
                  {ctaWidget &&
                    ctaWidget.sys.contentType.sys.id ===
                      'registrationEntrypoint' && (
                      <div className="desktop:mb-0 desktop:max-w-[350px]">
                        <CtaWidget
                          width={'100%'}
                          {...ctaWidget?.fields}
                          sysId={ctaWidget?.sys?.contentType?.sys?.id}
                          id="fullwidth-hero-cta"
                          module="Hero"
                          isMobile={false}
                          responsive
                        />
                      </div>
                    )}
                  {ctaWidget &&
                    ctaWidget.sys.contentType.sys.id === 'modalRevealCta' && (
                      <div className="max-w-[350px] desktop:mb-0">
                        <ModalRevealCTA {...(ctaWidget.fields as any)} />
                      </div>
                    )}
                  {privacyPolicyDisclaimer && (
                    <div className="mt-2 max-w-[350px] tablet:max-w-[467px] desktop:max-w-[435px]">
                      <PrivacyPolicyDisclaimer
                        privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                        displayPrivacyPolicyIcon={false}
                        color={'white'}
                        noMargin
                      />
                    </div>
                  )}
                  {shareIcons && (
                    <div className="mt-5">
                      <SocialShare inline hideShareCopy socialOptions={'all'} />
                    </div>
                  )}
                </animated.div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdditionalContent additionalContent={additionalContent} />
    </div>
  );
}
